import { HttpClient, httpResource } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { tap } from 'rxjs'
import { env } from '../../environments/environment'
import {
  EkvArbeitsnotiz,
  ErstgenehmigungAusBisherigemSystem,
  EkvAufgabe,
  EkvAufgabeAnlegen,
  EkvBoard,
  EkvKiEinschaetzung,
  EkvKiFeedbackReq,
  EkvKiBeobachtung,
  EkvKiBeobachtungEingabe,
  EkvKiBeobachtungen,
  EkvAuswertung,
  EkvNotizCreate,
  EkvOpenCounter,
  EkvReviewReq,
  EkvZuweisung,
  ManualEkvDicision,
  NeuBewertungPruefung,
  UploadGenehmigungReq,
  ZuzahlungsbefreiungUpdate,
} from '../intern/model/intern-backend.model'

export enum EkvDecision {
  STOP_PROCESS = 'STOP_PROCESS',
  POST_NEW_EKV = 'POST_NEW_EKV',
  MARK_EKV_AS_OKAY_AND_PROCEED = 'MARK_EKV_AS_OKAY_AND_PROCEED',
  ASSIGN_TO_LEISTUNGSERBRINGER = 'ASSIGN_TO_LEISTUNGSERBRINGER',
  /**
   * Nur im Board, NICHT im Backend-Enum der Entscheidung: dafuer gibt es einen eigenen Endpunkt
   * (`alsFolgeversorgungFuehren$`), weil die Versorgung danach neu gerechnet und der KV neu bewertet
   * wird. Wer `himiEkvDecision$` damit aufruft, bekommt eine 400.
   */
  ALS_FOLGEVERSORGUNG_FUEHREN = 'ALS_FOLGEVERSORGUNG_FUEHREN',
  /** Ebenfalls nur im Board, mit eigenem Endpunkt (`neuBewerten$`). */
  NEU_BEWERTEN = 'NEU_BEWERTEN',
}

@Injectable({
  providedIn: 'root',
})
export class EkvService {
  readonly apiUrl = env().api.internalUrl
  readonly http = inject(HttpClient)

  readonly ekvList = httpResource<ManualEkvDicision[]>(() => `${this.apiUrl}/ekv`)
  readonly ekvSaniUpCount = httpResource<EkvOpenCounter>(() => `${this.apiUrl}/ekv/counter?assignee=SANIUP`)

  /**
   * Rohstoff der Auswertung: jede Übergabe an einen Leistungserbringer und was daraus wurde. Lädt
   * nur, solange `aktiv()` stimmt (der Reiter offen ist), und bei jedem Öffnen frisch. Im
   * Injektionskontext aufrufen (Feld-Initialisierer).
   */
  auswertungLaden(aktiv: () => boolean) {
    return httpResource<EkvAuswertung>(() => (aktiv() ? `${this.apiUrl}/ekv/auswertung` : undefined))
  }

  private reloadEkv() {
    this.ekvList.reload()
    this.ekvSaniUpCount.reload()
  }

  phmEkvDecision$(sanitaetshausId: string, pflegeboxOrderId: string, ekvDecision: EkvDecision, note?: string) {
    // EKV decision ist ein enum (string) und muss explizit in ein JSON-Objekt umgewandelt werden
    const body = JSON.stringify(ekvDecision)
    const headers = { 'Content-Type': 'application/json' }
    const params = note ? { note } : undefined
    return this.http
      .post(`${this.apiUrl}/ekv/pflegebox/${sanitaetshausId}/${pflegeboxOrderId}`, body, { headers, params })
      .pipe(tap(() => this.reloadEkv()))
  }

  himiEkvDecision$(sanitaetshausId: string, himiVersorgungsId: string, ekvDecision: EkvDecision, note?: string) {
    // EKV decision ist ein enum (string) und muss explizit in ein JSON-Objekt umgewandelt werden
    const body = JSON.stringify(ekvDecision)
    const headers = { 'Content-Type': 'application/json' }
    const params = note ? { note } : undefined
    return this.http
      .post(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}`, body, { headers, params })
      .pipe(tap(() => this.reloadEkv()))
  }

  /**
   * „Als Folgeversorgung führen“: derselbe Leistungserbringer hat die Patient:in schon vor SaniUp
   * versorgt. Die Versorgung rechnet danach mit der Folgeposition, und der Deamon bewertet den KV neu
   * (genehmigungsfrei: erledigt und Lieferung, sonst ein neuer KV mit Kennzeichen 10). Nur HiMi.
   * Die Genehmigungsnummer der Erstversorgung ist optional.
   */
  alsFolgeversorgungFuehren$(sanitaetshausId: string, himiVersorgungsId: string, erstgenehmigung?: ErstgenehmigungAusBisherigemSystem) {
    return this.http
      .post<void>(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/als-folgeversorgung`, erstgenehmigung ?? {})
      .pipe(tap(() => this.reloadEkv()))
  }

  /**
   * „Neu bewerten“: die Kasse hat storniert, weil die Position genehmigungsfrei ist, und der Vertrag
   * ist korrigiert. Der Deamon bewertet den KV wie bei der ersten Einreichung, ohne Genehmigungspflicht
   * ist er erledigt und die Lieferung startet. Nur HiMi, 409 solange der Vertrag noch eine Genehmigung
   * verlangt.
   */
  neuBewerten$(sanitaetshausId: string, himiVersorgungsId: string) {
    return this.http
      .post<void>(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/neu-bewerten`, {})
      .pipe(tap(() => this.reloadEkv()))
  }

  /** Vorabprüfung für „Neu bewerten“: verlangt der Vertrag noch eine Genehmigung? Ein Abrechnungslauf im Backend. */
  neuBewertenPruefung$(sanitaetshausId: string, himiVersorgungsId: string) {
    return this.http.get<NeuBewertungPruefung>(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/neu-bewerten/pruefung`)
  }

  phmEkvReview$(sanitaetshausId: string, phmId: string, review: EkvReviewReq) {
    return this.http.post<void>(`${this.apiUrl}/ekv/pflegebox/${sanitaetshausId}/${phmId}/review`, review).pipe(tap(() => this.reloadEkv()))
  }
  himiEkvReview$(sanitaetshausId: string, himiId: string, review: EkvReviewReq) {
    return this.http.post<void>(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiId}/review`, review).pipe(tap(() => this.reloadEkv()))
  }

  adjustPg51Amount$(sanitaetshausId: string, phmId: string, newPg51Amount: number) {
    return this.http
      .post<void>(
        `${this.apiUrl}/ekv/pflegebox/${sanitaetshausId}/${phmId}/adjust-pg51-amount`,
        {},
        { params: { newAmount: newPg51Amount } },
      )
      .pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Erfasst eine analog (per Brief/Fax) eingegangene Genehmigung samt Bescheid.
   *
   * Bewusst hier und nicht nur im Leistungserbringer-Portal: bis 09/2026 lag der Upload dort
   * ausschliesslich im Panel "Pruefung Kostenvoranschlag", also genau in dem Zustand
   * unerreichbar, in dem SaniUp den Rueckläufer selbst prueft. Der Operator musste den Fall
   * erst per "Dem Leistungserbringer zuweisen" aus der Hand geben.
   */
  uploadGenehmigungHimi$(sanitaetshausId: string, himiVersorgungsId: string, metadata: UploadGenehmigungReq, pdf: File) {
    return this.http
      .post<void>(
        `${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/upload-genehmigung`,
        buildGenehmigungFormData(metadata, pdf),
      )
      .pipe(tap(() => this.reloadEkv()))
  }

  uploadGenehmigungPflegebox$(sanitaetshausId: string, pflegeboxOrderId: string, metadata: UploadGenehmigungReq, pdf: File) {
    return this.http
      .post<void>(
        `${this.apiUrl}/ekv/pflegebox/${sanitaetshausId}/${pflegeboxOrderId}/upload-genehmigung`,
        buildGenehmigungFormData(metadata, pdf),
      )
      .pipe(tap(() => this.reloadEkv()))
  }

  refetchHimiEkv$(sanitaetshausId: string, himiVersorgungsId: string) {
    return this.http
      .post<void>(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/refetch`, {})
      .pipe(tap(() => this.reloadEkv()))
  }

  refetchPflegeboxEkv$(sanitaetshausId: string, pflegeboxOrderId: string) {
    return this.http
      .post<void>(`${this.apiUrl}/ekv/pflegebox/${sanitaetshausId}/${pflegeboxOrderId}/refetch`, {})
      .pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Entscheidungsbescheid der Kasse als PDF-Blob.
   *
   * Antwortet mit **204** (leerer Body), wenn kein Bescheid hinterlegt ist — das ist kein Fehler,
   * sondern der Normalfall bei einer Sofort-Ablehnung. Der Aufrufer erkennt es am `size === 0`
   * (dieselbe Falle wie beim Leistungserbringer-Logo: ein leerer Blob ist ein gültiger Wert).
   *
   * Ohne `dokumentId` der jüngste Bescheid, mit ihr der einer früheren Runde (Reiter „Verlauf“).
   */
  ekvDecisionPdf$(vorgang: EkvVorgang, dokumentId?: string) {
    const params: Record<string, string> = dokumentId ? { dokumentId } : {}
    return this.http.get(`${vorgangUrl(this.apiUrl, vorgang)}/entscheidung-pdf`, { responseType: 'blob', params })
  }

  /**
   * Setzt die Zuzahlungsbefreiung des Patienten (absolut — die Liste ersetzt die bisherigen Jahre).
   * Leeres Array heisst „keine Befreiung" und ist ein gültiger Wert.
   */
  updateCopaymentStatus$(vorgang: EkvVorgang, years: number[]) {
    const body: ZuzahlungsbefreiungUpdate = { yearsWithCoPaymentExemption: years }
    return this.http.post<void>(`${vorgangUrl(this.apiUrl, vorgang)}/zuzahlungsstatus`, body).pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Interne Arbeitsnotiz anlegen. Autor und Zeitpunkt setzt das Backend aus Token und Serveruhr,
   * deshalb geht nur der Text hinaus — und deshalb kommt die fertige Notiz zurück.
   *
   * Alle vier Methoden hier laden die Liste neu, weil Notizen und Bearbeiter Teil von
   * `ManualEkvDicision` sind: nur so sieht die Tabelle hinter dem Dialog die Änderung sofort.
   */
  addNotiz$(vorgang: EkvVorgang, text: string) {
    const body: EkvNotizCreate = { text }
    return this.http.post<EkvArbeitsnotiz>(`${vorgangUrl(this.apiUrl, vorgang)}/notiz`, body).pipe(tap(() => this.reloadEkv()))
  }

  deleteNotiz$(vorgang: EkvVorgang, notizId: string) {
    return this.http.delete<void>(`${vorgangUrl(this.apiUrl, vorgang)}/notiz/${notizId}`).pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Den Rückläufer persönlich übernehmen. Ohne Body: wer übernimmt, steht im Token.
   *
   * Für die Übergabe an eine andere Person gibt es `zuweisen$` — die braucht den Empfänger
   * naturgemäss als Feld.
   */
  uebernehmen$(vorgang: EkvVorgang) {
    return this.http.post<void>(`${vorgangUrl(this.apiUrl, vorgang)}/bearbeiter`, {}).pipe(tap(() => this.reloadEkv()))
  }

  freigeben$(vorgang: EkvVorgang) {
    return this.http.delete<void>(`${vorgangUrl(this.apiUrl, vorgang)}/bearbeiter`).pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Den Rückläufer jemand anderem geben, optional mit einem Satz zur Übergabe.
   *
   * Beides geht in EINEM Aufruf hinaus, nicht als „zuweisen, dann Notiz": das Backend schreibt sie
   * zusammen in einem Speichervorgang, sonst könnte auf dem grossen Versorgungsdokument (kein OCC
   * in Cosmos) die Notiz verlorengehen, während die Zuweisung steht.
   *
   * Wer zugewiesen hat, setzt der Server aus dem Token — deshalb geht nur der Empfänger hinaus.
   */
  zuweisen$(vorgang: EkvVorgang, kennung: string, notiz?: string) {
    const body: EkvZuweisung = { kennung, notiz: notiz?.trim() || undefined }
    return this.http.post<void>(`${vorgangUrl(this.apiUrl, vorgang)}/bearbeiter/zuweisen`, body).pipe(tap(() => this.reloadEkv()))
  }

  /**
   * KI-Einschaetzung neu erstellen. Synchron und langsam (30 bis 120 Sekunden): das Modell liest den
   * Bescheid. Im Normalfall erstellt der Scheduler sie von selbst, der Knopf ist fuer „nach meiner
   * Notiz noch einmal" gedacht. Die Antwort reist danach ohnehin mit der Liste mit.
   */
  kiEinschaetzen$(vorgang: EkvVorgang) {
    return this.http.post<EkvKiEinschaetzung>(`${vorgangUrl(this.apiUrl, vorgang)}/ki-einschaetzung`, {}).pipe(tap(() => this.reloadEkv()))
  }

  /**
   * Rückmeldung zur Einschätzung: das Urteil und, bei „Nein, richtig wäre …", die Empfehlung, die
   * gegolten hätte. Wer urteilt, setzt der Server aus dem Token. Notizen haben einen eigenen Weg
   * ({@link kiNotieren$}), weil es mehrere je Fall geben darf und sie einen Repost überleben müssen.
   */
  kiFeedback$(vorgang: EkvVorgang, rueckmeldung: EkvKiFeedbackReq) {
    const body: EkvKiFeedbackReq = {
      urteil: rueckmeldung.urteil,
      korrekturEmpfehlung: rueckmeldung.korrekturEmpfehlung,
      kommentar: rueckmeldung.kommentar?.trim() || undefined,
    }
    return this.http.post<void>(`${vorgangUrl(this.apiUrl, vorgang)}/ki-einschaetzung/feedback`, body).pipe(tap(() => this.reloadEkv()))
  }

  /** Die Notizen zu EINEM Rückläufer. Im Injektionskontext der Karte aufrufen (Feld-Initialisierer). */
  kiBeobachtungenZu(versorgungsId: () => string) {
    return httpResource<EkvKiBeobachtungen>(
      () => `${this.apiUrl}/ekv/ki-beobachtungen?versorgungsId=${encodeURIComponent(versorgungsId())}`,
    )
  }

  kiNotieren$(vorgang: EkvVorgang, text: string) {
    const body: EkvKiBeobachtungEingabe = { text: text.trim() }
    return this.http.post<EkvKiBeobachtung>(`${vorgangUrl(this.apiUrl, vorgang)}/ki-beobachtungen`, body)
  }

  // ---- Entscheidungsboard der Detailseite ----

  /**
   * Was das Board neben der Arbeitsliste braucht: Patientin mit Befreiungsjahren und Ausweisen,
   * Verordnungen, Aufgaben an der Versorgung, frühester Lieferstart. Ein Abruf je geöffnetem
   * Vorgang, nicht mit der Liste (dort wäre jedes Feld ein N+1 über den ganzen Bestand).
   */
  ekvBoard$(vorgang: EkvVorgang) {
    return this.http.get<EkvBoard>(`${vorgangUrl(this.apiUrl, vorgang)}/board`)
  }

  /** Scan einer Verordnung als PDF-Blob. Nur Hilfsmittel haben eine. */
  ekvVerordnungPdf$(sanitaetshausId: string, himiVersorgungsId: string, rezeptId: string) {
    return this.http.get(`${this.apiUrl}/ekv/himi/${sanitaetshausId}/${himiVersorgungsId}/verordnung/${rezeptId}`, { responseType: 'blob' })
  }

  /**
   * Aufgabe für den Leistungserbringer, von SaniUp: mit `bearbeiterId` an die Ansprechperson aus dem
   * Board, ohne in den Topf „Offen für alle“. Mit
   * `kiVorschlagIndex` aus einem Vorschlag der KI; das Backend vermerkt dann am Vorschlag, ob er
   * unverändert passte, deshalb lädt die Liste (sie trägt die Einschätzung) mit neu.
   */
  aufgabeAnlegen$(vorgang: EkvVorgang, aufgabe: EkvAufgabeAnlegen) {
    return this.http.post<EkvAufgabe>(`${vorgangUrl(this.apiUrl, vorgang)}/aufgaben`, aufgabe).pipe(tap(() => this.reloadEkv()))
  }
}

/**
 * Was einen Rückläufer adressiert. Eine Zeile der Liste (`ManualEkvDicision`) erfüllt den Typ selbst
 * und kann direkt übergeben werden. Bewusst ein Objekt statt drei Parametern: Haus und Versorgung
 * sind beide Strings, vertauscht kompilierte das still und landete beim falschen Dokument.
 */
export type EkvVorgang = Pick<ManualEkvDicision, 'versorgungsType' | 'versorgungsId' | 'sanitaetshausId'>

/**
 * HiMi und Pflegebox liegen in getrennten Containern; das Backend nimmt deshalb je Typ einen
 * eigenen Pfad entgegen und rät nie über beide Repositories (Backend-Regel „Versorgung laden").
 * Die `sanitaetshausId` im Pfad ist der Partition Key: ohne ihn liest das Backend die Versorgung
 * cross-partition über alle Häuser.
 */
function vorgangUrl(apiUrl: string, vorgang: EkvVorgang): string {
  const segment = vorgang.versorgungsType === 'PFLEGEBOX_VERSORGUNG' ? 'pflegebox' : 'himi'
  return `${apiUrl}/ekv/${segment}/${vorgang.sanitaetshausId}/${vorgang.versorgungsId}`
}

/**
 * Multipart wie im Leistungserbringer-Portal: der JSON-Teil geht als Blob mit gesetztem
 * Content-Type raus, sonst nimmt Spring ihn als `text/plain` an und der `@RequestPart` bleibt leer.
 */
function buildGenehmigungFormData(metadata: UploadGenehmigungReq, pdf: File): FormData {
  const formData = new FormData()
  formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }))
  formData.append('pdf', pdf)
  return formData
}
